import React, { useState, useEffect } from 'react';
import { PERSON_ROLE_PENDING_OWNER, PERSON_ROLE_OWNER } from 'pages/Administrators/constants';
import makeStyles from '@material-ui/core/styles/makeStyles';
import colors from 'styles/colors';
import { useDispatch } from 'react-redux';
import makeSetPassword from 'fetches/makeSetPassword';
import makeUpdateRole from 'fetches/makeUpdateRole';
import _get from 'lodash/get';
import { useStatus, isLoading, isNormal } from 'resift';
import showSnackbar from 'helpers/showSnackbar';
import usePrevious from '@sift/resift/usePrevious';

// Components
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Typography from '@material-ui/core/Typography';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Slide from '@material-ui/core/Slide';
import { TransitionProps } from '@material-ui/core/transitions';
import SiftLogo from '@sift/skipper/components/SiftLogo';
import PasswordTextField from './PasswordTextField';
import HelpArticleLink from 'components/Page/HelpArticleLink';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface Props {
  role: number;
  userId: string;
  hasSetPassword: boolean;
}

function AcceptOwnerRoleDialog({ role, userId, hasSetPassword }: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const setPassword = makeSetPassword(userId);
  const updateRole = makeUpdateRole(userId, 'accept-owner-role');
  const setPasswordStatus = useStatus(setPassword);
  const prevSetPasswordStatus = usePrevious(setPasswordStatus);
  const updateRoleStatus = useStatus(updateRole);
  const prevUpdateRoleStatus = usePrevious(updateRoleStatus);
  const shouldOpenDialog = role === PERSON_ROLE_PENDING_OWNER;
  const [open, setOpen] = useState(shouldOpenDialog);
  const [passwordInput, setPasswordInput] = useState('');
  const [secondPasswordInput, setSecondPasswordInput] = useState('');
  const [passwordIsValid, setPasswordIsValid] = useState(false);
  const [showSetPasswordForm, setShowSetPasswordForm] = useState(!hasSetPassword);

  showSnackbar({
    status: setPasswordStatus,
    message: 'Password successfully updated.',
    errorMessage: 'An error occurred while setting the password.',
  });

  showSnackbar({
    status: updateRoleStatus,
    message: 'You have successfully accepted the owner role.',
    errorMessage: 'An error occurred while accepting the owner role.',
  });

  useEffect(() => {
    setOpen(shouldOpenDialog);
  }, [updateRoleStatus]);

  useEffect(() => {
    if (isLoading(prevSetPasswordStatus) && isNormal(setPasswordStatus)) {
      handleAcceptOwnerRole();
    }
  }, [prevSetPasswordStatus, setPasswordStatus]);

  useEffect(() => {
    if (isLoading(prevUpdateRoleStatus) && isNormal(updateRoleStatus)) {
      setOpen(false);
    }
  }, [prevUpdateRoleStatus, updateRoleStatus]);

  const handleSubmitNewPassword = async () => {
    if (checkNewPasswordCriteria()) {
      await dispatch(setPassword(passwordInput));
    }
  };

  const handleShowUpdatePasswordForm = () => {
    setShowSetPasswordForm(true);
  };

  const handleCancelSetPassword = () => {
    setShowSetPasswordForm(false);
  };

  const checkNewPasswordCriteria = () => {
    return passwordIsValid && passwordInput === secondPasswordInput;
  };

  const setPasswordCallback = (password: string) => {
    setPasswordInput(password);
  };

  const setValidPasswordCallback = (validPassword: boolean) => {
    setPasswordIsValid(validPassword);
  };

  const handleChangeTwo = (event: any) => {
    setSecondPasswordInput(event.target.value);
  };

  const checkPasswordsMatch = () => {
    if (secondPasswordInput.length !== 0 && passwordInput !== secondPasswordInput) {
      return 'Passwords do not match.';
    } else {
      return <br />;
    }
  };

  const handleAcceptOwnerRole = async () => {
    await dispatch(updateRole(PERSON_ROLE_OWNER));
  };

  const renderSetPasswordContent = () => {
    return (
      <>
        <Typography className={classes.descriptions}>
          Owners are required to set a dedicated Sift password. Please set a password below.
        </Typography>
        <Typography className={classes.descriptions}>
          Passwords must be at least 8 characters long and include at least one lowercase letter,
          one uppercase letter, and one number.
        </Typography>
        <div className={classes.textFields}>
          <PasswordTextField
            passwordCallback={setPasswordCallback}
            password={passwordInput}
            validPasswordCallback={setValidPasswordCallback}
            label="New Password"
          />
          <FormControl className={classes.inputTwo}>
            <InputLabel className={classes.labelSize}>Retype New Password</InputLabel>
            <Input
              className={classes.inputSize}
              type="password"
              onChange={handleChangeTwo}
              value={secondPasswordInput}
            />
          </FormControl>
        </div>

        <FormHelperText error className={classes.passwordMessage}>
          {checkPasswordsMatch()}
        </FormHelperText>
        <div className={classes.buttonRow}>
          <Button onClick={handleCancelSetPassword} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitNewPassword}
            disabled={!checkNewPasswordCriteria() || isLoading(setPasswordStatus)}
            color="secondary"
            variant="contained"
            type="submit"
          >
            {isLoading(setPasswordStatus) ? <>Sending Request</> : <>Accept Role</>}
          </Button>
        </div>
      </>
    );
  };

  return (
    <Dialog fullScreen open={open} TransitionComponent={Transition}>
      <AppBar className={classes.appBar}>
        <Toolbar className={classes.toolBar}>
          <SiftLogo height={24} backgroundColor={colors.sidebarGray} />
        </Toolbar>
      </AppBar>
      <div className={classes.wrapper}>
        <Typography variant={'h2'} className={classes.pageTitle}>
          You’ve been added as an Owner of Sift
        </Typography>
        <Typography className={classes.descriptions}>
          As an owner of Sift, you will have access to all administrative features and will be
          protected from being removed during a data sync.
        </Typography>
        <HelpArticleLink
          link={'https://help.justsift.com/en/articles/3931941-administrators'}
          text="Learn more about roles ❯"
        />
        <div className={classes.passwordSection}>
          {showSetPasswordForm ? (
            renderSetPasswordContent()
          ) : (
            <>
              <Typography className={classes.descriptions}>
                Owners are required to set a dedicated Sift password. Click the below button if you
                would like to update your password.
              </Typography>
              <Button
                onClick={handleShowUpdatePasswordForm}
                color="secondary"
                variant="outlined"
                className={classes.updateRoleButton}
              >
                Update Password
              </Button>
              <Button
                disabled={!hasSetPassword || isLoading(updateRoleStatus)}
                color="secondary"
                variant="contained"
                onClick={handleAcceptOwnerRole}
                className={classes.acceptRoleButton}
              >
                {isLoading(updateRoleStatus) ? <>Sending Request</> : <>Accept Role</>}
              </Button>
            </>
          )}
        </div>
      </div>
    </Dialog>
  );
}

const useStyles = makeStyles((theme: any) => ({
  root: {},
  wrapper: {
    maxWidth: 720,
    width: '100%',
    margin: '0 auto',
    padding: theme.spacing(4, 2, 2, 2),
    marginBottom: theme.spacing(8),
  },
  acceptRoleButton: {
    marginTop: theme.spacing(3),
  },
  updateRoleButton: {
    display: 'block',
    marginTop: theme.spacing(4),
  },
  pageTitle: {
    ...theme.typography.heading2,
    marginBottom: theme.spacing(4),
  },
  descriptions: {
    marginBottom: theme.spacing(2),
  },
  toolBar: {
    backgroundColor: theme.colors.sidebarGray,
  },
  appBar: {
    position: 'relative',
  },
  inputTwo: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  labelSize: {
    fontSize: 25,
    paddingTop: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
      marginTop: theme.spacing(1),
    },
  },
  inputSize: {
    // TODO Pearl for later: improve theme.typography and add these
    fontSize: 28,
    minHeight: 70,
    paddingTop: theme.spacing(1),
  },
  passwordSection: {
    marginTop: theme.spacing(4),
  },
  passwordMessage: {
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(3.5),
    fontSize: 15,
  },
  textFields: {
    width: '60%',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  buttonRow: {
    '& > button': {
      marginRight: theme.spacing(),
    },
  },
}));

export default AcceptOwnerRoleDialog;
